import { getSeparator } from "@components/Byline/helpers";
import { CardVariations } from "@particles/card/utils/types";
import ArticleBylineImage from "@routes/Article/components/ArticleByline/ArticleBylineImage";
import ArticleBylineName from "@routes/Article/components/ArticleByline/ArticleBylineName";
import ArticleTimeStamp from "@routes/Article/components/ArticleTimeStamp";
import { FeedAuthor } from "@routes/Author/utils/types";
import { cn } from "@util/helpers";

type ArticleBylineProps = {
  /** List of authors for the article */
  authors: FeedAuthor[];
  /** Published date of the article */
  updatedTime: string;
  /** Updated date of the article */
  editedTime?: string;
  /** Flag to indicate if the updated time should be displayed */
  hasUpdatedTimeDisplayed?: boolean;
  /** Optional URL parameters for the author links */
  linkParams?: URLSearchParams;
  /** Optional backdrop class for styling */
  backdrop?: string;
  /** Variations for styling */
  variations?: CardVariations;
} & Pick<CardVariations, "byline">;

/**
 * ArticleByline component that displays the byline information of an article,
 * including authors, published date, and updated date.
 */
const ArticleByline = ({
  authors,
  updatedTime,
  editedTime,
  hasUpdatedTimeDisplayed,
  linkParams,
  backdrop,
  byline,
  variations,
}: ArticleBylineProps): React.ReactElement => {
  const authorsCount = authors.length;

  const authorWithoutByline = authors.find((authors) => {
    return !authors?.image;
  });

  if (authorsCount === 0) {
    return (
      <ArticleTimeStamp
        updatedTime={updatedTime}
        editedTime={editedTime || ""}
        hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
      />
    );
  }

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-start gap-2",
        byline?.extraClass
      )}
      data-testid="article-byline-component"
    >
      {authorsCount >= 1 && authorsCount <= 3 && !authorWithoutByline ? (
        <div
          className={cn(
            "flex flex-shrink-0 flex-row items-center justify-start",
            byline?.imageClassName
          )}
        >
          {authors?.map((author, index) => {
            return (
              <ArticleBylineImage
                key={author.id}
                author={author}
                imageWidth={variations?.byline?.imageWidth}
                imageHeight={variations?.byline?.imageHeight}
                className={cn(
                  "aspect-square overflow-hidden rounded-full",
                  { "-translate-x-2": index !== 0 },
                  backdrop
                )}
              />
            );
          })}
        </div>
      ) : null}

      <div
        className={cn(
          "flex flex-col flex-wrap",
          { "pl-2": !authorWithoutByline },
          byline?.nameClassName
        )}
      >
        <div className="flex flex-wrap gap-x-1.5">
          {authors?.map((author, index) => {
            return (
              <ArticleBylineName
                key={author.id}
                author={author}
                separator={getSeparator(index, authorsCount)}
                className={cn(
                  "word-break align-middle font-poppins text-base font-semibold text-gray-850",
                  variations?.byline?.extraClass
                )}
                linkParams={linkParams}
                linkClassName={variations?.byline?.linkClassName}
              />
            );
          })}
        </div>

        {updatedTime ? (
          <ArticleTimeStamp
            updatedTime={updatedTime}
            editedTime={editedTime || ""}
            hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ArticleByline;
